// Service-worker update discovery for the installed PWA.
//
// vite-plugin-pwa (`registerType: 'autoUpdate'`, see vite.config.ts)
// registers the generated Workbox service worker with skipWaiting +
// clientsClaim. When a new SW is *fetched*, it activates and takes over
// the cache immediately, so a freshly-deployed build's hashed assets are
// served on the next full document load — without the surveyor having to
// clear cache (the original field-reported failure). cleanupOutdatedCaches
// then prunes the superseded precache.
//
// The gap this module closes is DISCOVERY on a long-lived install. A PWA
// run all shift only does client-side routing, so it never performs a
// full document navigation — and the browser's automatic SW update check
// is tied to navigations (plus a ~24 h ceiling). Without an explicit
// check, a build deployed mid-shift is never even fetched, so the cache
// stays stale and staff resorted to clearing it. We therefore call
// `registration.update()` on an interval AND whenever the tab regains
// visibility (the "reopened the app" moment), which fetches + installs
// the new SW in the background.
//
// We deliberately do NOT force-reload the open tab when the new SW
// activates. A capture tap is held in the capture client's 500 ms
// coalesce buffer (capture-client-powersync.ts) before it is persisted
// to SQLite / the upload queue; a programmatic `window.location.reload()`
// fired mid-count (e.g. when the interval below triggers an update while
// a surveyor is tapping) would destroy the document before that buffer
// flushes and silently drop the asset count — a contractual data-loss
// bug. Adoption of the new chunks therefore happens on the tab's next
// natural full load (relaunch / navigation), at which point the active
// SW serves the new assets. Instant in-tab adoption would require
// coordinating the reload with capture-client idleness and is left as a
// follow-up rather than risk a dropped count.
//
// Uses only standard `navigator.serviceWorker` APIs (no
// `virtual:pwa-register`), so it stays resolvable under the VitePWA-less
// vitest config and unit-testable with a mocked ServiceWorkerContainer.

// How often an open install re-checks for a freshly-deployed SW.
const UPDATE_POLL_INTERVAL_MS = 30 * 60 * 1000 // 30 minutes

export function registerServiceWorkerUpdates(): void {
  if (
    typeof window === 'undefined' ||
    typeof navigator === 'undefined' ||
    navigator.serviceWorker == null
  ) {
    return
  }

  // `ready` resolves with the active registration. Drive an update check
  // now (on visibility regain) and on a cadence, because an installed
  // PWA never navigates and would otherwise not discover a new deploy
  // for hours.
  navigator.serviceWorker.ready
    .then((registration) => {
      const checkForUpdate = (): void => {
        // Never throw from here: offline or a transient network failure
        // is expected, and the next poll / visibility regain retries.
        void registration.update().catch(() => undefined)
      }

      window.setInterval(checkForUpdate, UPDATE_POLL_INTERVAL_MS)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate()
      })
    })
    .catch(() => {
      // No active SW yet (unsupported browser, or a first-load race).
      // vite-plugin-pwa's own registration still installs it; this
      // module simply has nothing to poll on this load.
    })
}

// One reload per failed chunk URL. A genuinely-broken deploy (the SAME
// hashed chunk still won't load after the reload) recovers at most once and
// then stops instead of looping; but a LATER deploy in the same long-lived
// PWA session — whose chunks carry fresh hashes, i.e. new URLs — still
// recovers. Keyed per-URL rather than one session-wide flag, because a
// permanent flag leaves multi-deploy installs stuck on the second stale
// chunk, re-introducing the manual cache-clear failure this module removes.
// The set of already-reloaded URLs is JSON in sessionStorage: it survives
// reload() and resets when the PWA window closes.
const PRELOAD_RELOAD_GUARD_KEY = 'citycare:preload-error-reloaded-chunks'

// Pull the failed module URL out of a `vite:preloadError`. Vite attaches the
// rejected import() error as the event's `payload`; its message is
// "Failed to fetch dynamically imported module: <url>". We key the reload
// guard on that URL. When no URL is parseable we fall back to a constant, so
// the guard degrades to one-reload-per-session rather than throwing.
function failedChunkKey(event: Event): string {
  const payload = (event as Event & { payload?: unknown }).payload
  const message =
    payload instanceof Error ? payload.message : String(payload ?? '')
  const match = message.match(/\bhttps?:\/\/[^\s'"]+/)
  return match ? match[0] : 'citycare:unknown-chunk'
}

// Recover from a stale dynamic import after a background SW update.
//
// When `registerServiceWorkerUpdates` installs a new SW mid-session, the
// new SW activates (skipWaiting/clientsClaim) and `cleanupOutdatedCaches`
// prunes the superseded precache. The still-running OLD document then
// holds references to old hashed chunk URLs; if the user navigates to a
// not-yet-loaded lazy route — the admin pages in router.tsx are
// `React.lazy` — the import() fetches an old-hash chunk the new SW no
// longer caches and the freshly-deployed server no longer ships, so Vite
// raises `vite:preloadError`. Reloading swaps the document to the new
// build, which references the new chunk URLs.
//
// This is SAFE relative to the capture data-loss concern above: the
// capture screens are STATIC imports (in the main bundle), so navigating
// Base→Site→Building→Room never triggers a dynamic import and this never
// fires mid-capture. Only lazy admin-route navigation can trigger it.
//
// `reload` is injectable so tests exercise the behaviour without
// redefining `window.location`. Returns an unsubscribe for symmetry /
// test isolation; the app-level caller lets it live for the page
// lifetime.
export function registerChunkReloadOnPreloadError(
  reload: () => void = () => {
    window.location.reload()
  },
): () => void {
  if (typeof window === 'undefined') return () => undefined
  const handler = (event: Event): void => {
    const chunk = failedChunkKey(event)
    try {
      const raw = sessionStorage.getItem(PRELOAD_RELOAD_GUARD_KEY)
      const parsed: unknown = raw != null ? JSON.parse(raw) : []
      const reloaded: string[] = Array.isArray(parsed)
        ? parsed.filter((url): url is string => typeof url === 'string')
        : []
      // This exact chunk already triggered a reload and STILL won't load →
      // broken deploy. Stop and let Vite surface the error instead of
      // looping. A different chunk (a later deploy) is not yet recorded, so
      // it still recovers.
      if (reloaded.includes(chunk)) return
      sessionStorage.setItem(
        PRELOAD_RELOAD_GUARD_KEY,
        JSON.stringify([...reloaded, chunk]),
      )
    } catch {
      // sessionStorage blocked (private mode / sandbox) or a corrupt value —
      // fall through and still attempt a single reload.
    }
    event.preventDefault()
    reload()
  }
  window.addEventListener('vite:preloadError', handler)
  return () => {
    window.removeEventListener('vite:preloadError', handler)
  }
}
